import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const platformData = {
    instagram: {
        title: "Instagram — Locality hashtags + creator density",
        desc: "Instagram is where hyperlocal discovery happens fastest. Combine locality hashtags and outlet handles to understand which creators and formats win in each pocket.",
        look: [
            "Locality hashtags (#baner #wakad #aundh) + category tags",
            "Mentions and tags on outlet handles (local proof)",
            "Saves/Shares as intent proxies (local discovery)"
        ],
        use: [
            "Decide which localities need higher creator density",
            "Write briefs that match local language + culture",
            "Pick nano vs micro split based on pocket affordability"
        ]
    },
    youtube: {
        title: "YouTube — Local searches + deep trust formats",
        desc: "YouTube gives deeper signals: what people search, what questions they ask, and which creators build trust for your category in each city cluster.",
        look: [
            "City + category keyword clusters (titles/descriptions)",
            "FAQ-style comment patterns from local audiences",
            "Creators doing locality walkthroughs/reviews"
        ],
        use: [
            "Plan deeper consideration campaigns by city",
            "Create locality-specific “explainer” narratives",
            "Select creators for trust-led formats"
        ]
    },
    facebook: {
        title: "Facebook — Local community signals",
        desc: "Local groups and pages show real demand signals. Use them to understand sentiment, local objections, and the offers that trigger shares.",
        look: [
            "Local group topics and engagement spikes",
            "Offer/event posts with share velocity",
            "Comment sentiment and FAQs in local language"
        ],
        use: [
            "Refine offers and messaging by locality",
            "Catch issues early (reputation risk)",
            "Support event-based local activations"
        ]
    },
    x: {
        title: "X (Twitter) — Real-time local pulse",
        desc: "Track fast-moving spikes around local events, launches, and issues. Useful when timing matters or you need rapid response.",
        look: [
            "Mentions + city keywords",
            "Event hashtags and spikes by time",
            "Local voices driving conversations"
        ],
        use: [
            "Time-sensitive creative choices",
            "Competitor monitoring during launches",
            "PR and response alignment"
        ]
    },
    linkedin: {
        title: "LinkedIn — City ecosystem narratives",
        desc: "Best for premium/B2B and reputation narratives. Understand what resonates with the city’s professional ecosystem.",
        look: [
            "Local business initiatives and partnerships",
            "Category narratives from leaders and companies",
            "High-signal posts shaping perception"
        ],
        use: [
            "Partnership discovery in a city ecosystem",
            "Thought-leadership angles for local credibility",
            "Employer branding narratives"
        ]
    }
};

const MediaAnalytics = () => {
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [activePlatform, setActivePlatform] = useState('instagram');
    const currentPlatform = platformData[activePlatform];

    return (
        <div className="bg-white text-slate-900 font-sans">
            <style>
                {`
          .glass { backdrop-filter: blur(10px); background: rgba(255,255,255,.82); }
          .shadow-soft { box-shadow: 0 14px 40px rgba(2,6,23,.08); }
          .ring-soft { box-shadow: 0 0 0 1px rgba(15,23,42,.06); }
        `}
            </style>

            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/80 glass">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-xl bg-slate-900"></div>
                            <span className="text-base font-semibold tracking-tight">Oye Creators</span>
                        </Link>

                        <nav className="hidden items-center gap-6 md:flex">
                            <Link className="text-sm font-medium text-slate-700 hover:text-slate-900" to="/product/discover-creators">Product</Link>
                            <Link className="text-sm font-medium text-slate-700 hover:text-slate-900" to="#">Solutions</Link>
                            <Link className="text-sm font-medium text-slate-700 hover:text-slate-900" to="#">Resources</Link>
                            <Link className="text-sm font-medium text-slate-700 hover:text-slate-900" to="/pricing-page">Pricing</Link>
                        </nav>

                        <div className="hidden items-center gap-3 md:flex">
                            <Link to="/signin" className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Sign in</Link>
                            <a href="#cta" className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">
                                Request demo
                            </a>
                        </div>

                        <button
                            id="mobileBtn"
                            className="md:hidden rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold"
                            onClick={() => setMobileNavOpen(!mobileNavOpen)}
                        >
                            Menu
                        </button>
                    </div>

                    {/* Mobile Nav */}
                    {mobileNavOpen && (
                        <div id="mobileNav" className="border-t border-slate-100 py-4 md:hidden">
                            <div className="flex flex-col gap-2">
                                <Link className="rounded-xl px-3 py-2 text-sm font-medium hover:bg-slate-50" to="/product/discover-creators">Product</Link>
                                <Link className="rounded-xl px-3 py-2 text-sm font-medium hover:bg-slate-50" to="#">Solutions</Link>
                                <Link className="rounded-xl px-3 py-2 text-sm font-medium hover:bg-slate-50" to="#">Resources</Link>
                                <Link className="rounded-xl px-3 py-2 text-sm font-medium hover:bg-slate-50" to="/pricing-page">Pricing</Link>
                                <div className="mt-2 flex gap-2">
                                    <Link to="/signin" className="w-1/2 rounded-xl border border-slate-200 px-4 py-2 text-center text-sm font-semibold">Sign in</Link>
                                    <a href="#cta" className="w-1/2 rounded-xl bg-slate-900 px-4 py-2 text-center text-sm font-semibold text-white">Request demo</a>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Hero */}
            <section className="relative">
                <div className="mx-auto max-w-7xl px-4 pb-10 pt-12 sm:px-6 lg:px-8 lg:pb-16 lg:pt-16">
                    <div className="grid items-center gap-10 lg:grid-cols-2">
                        <div>
                            <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700">
                                Market Analysis • Hyperlocal Playbook
                            </div>

                            <h1 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl">
                                Know what works in each pocket—before you spend
                            </h1>

                            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                                Oye Creators Market Analysis is built for India-first hyperlocal marketing.
                                It helps you understand what content, creators, and narratives work in
                                <span className="font-semibold text-slate-900"> specific city clusters and localities </span>
                                (not generic “India-wide” assumptions).
                            </p>

                            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                                <a href="#hyperlocal" className="rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 text-center">
                                    See hyperlocal insights
                                </a>
                                <a href="#cta" className="rounded-2xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 text-center">
                                    Request demo
                                </a>
                            </div>

                            <div className="mt-8 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                <span className="rounded-full border border-slate-200 px-3 py-1">City clusters (Pune, PCMC, Mumbai…)</span>
                                <span className="rounded-full border border-slate-200 px-3 py-1">Localities (Baner, Wakad, Aundh…)</span>
                                <span className="rounded-full border border-slate-200 px-3 py-1">Language & culture signals</span>
                                <span className="rounded-full border border-slate-200 px-3 py-1">Creator density & affordability</span>
                            </div>

                            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                                Notes: Insights depend on data availability, platform policies, and the integrations you configure.
                            </div>
                        </div>

                        <div>
                            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-100 shadow-soft"></div>
                            <p className="mt-3 text-xs text-slate-500">
                                Replace with screenshot: “City cluster selector + locality insights + trend cards + competitor compare”.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Hyperlocal definition */}
            <section id="hyperlocal" className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:p-10">
                    <div className="grid gap-8 lg:grid-cols-3">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">What “hyperlocal” means</div>
                            <h2 className="mt-2 text-2xl font-semibold tracking-tight">Markets are not cities. Cities are not localities.</h2>
                            <p className="mt-3 text-slate-600">
                                A single city can contain multiple micro-markets with different languages, price sensitivity, creator ecosystems,
                                and consumer intent. Oye Creators Market Analysis is designed to help you plan for these micro-markets.
                            </p>
                        </div>

                        <div className="rounded-3xl border border-slate-100 bg-slate-50 p-6">
                            <div className="text-sm font-semibold">Input signals we analyze</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-700">
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>City cluster + localities + pin-code cues</li>
                                <li className="flex gap-3"><span class="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Language mix (Hindi/Marathi/English)</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Creator density & tier availability</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Category trends & seasonality</li>
                            </ul>
                        </div>

                        <div className="rounded-3xl border border-slate-100 bg-slate-50 p-6">
                            <div className="text-sm font-semibold">Outputs you can action</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-700">
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Locality-wise content angles</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Creator shortlist strategy by pocket</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Budget & tier mix recommendations</li>
                                <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Competitor tactics by geography</li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-8 rounded-3xl border border-slate-100 bg-white p-6">
                        <div className="text-sm font-semibold">Example: how planning changes by pocket</div>
                        <div className="mt-3 grid gap-3 lg:grid-cols-3">
                            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pocket</div>
                                <div className="mt-1 text-sm font-semibold">Pune • Baner</div>
                                <p className="mt-2 text-sm text-slate-600">Premium cafés, coworking, fitness. Content tends to be “aspirational + utility”.</p>
                            </div>
                            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pocket</div>
                                <div className="mt-1 text-sm font-semibold">PCMC • Wakad</div>
                                <p className="mt-2 text-sm text-slate-600">Family-first consumption, value narratives. Content tends to be “deal + convenience”.</p>
                            </div>
                            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pocket</div>
                                <div className="mt-1 text-sm font-semibold">Pune • Kothrud</div>
                                <p className="mt-2 text-sm text-slate-600">Legacy audience + students. Content tends to be “trust + relatability”.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Modules (hyperlocal-first) */}
            <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                <div className="grid gap-6 lg:gap-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-semibold tracking-tight">What you can analyze—hyperlocal-first</h2>
                        <p className="mx-auto mt-3 max-w-3xl text-slate-600">
                            Market Analysis is structured around planning questions: where to activate, which creator tiers to use, which hooks to write,
                            and how competitors behave in each pocket.
                        </p>
                    </div>

                    {/* Module 1: Locality Opportunity Map */}
                    <div className="grid items-center gap-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:grid-cols-2 lg:p-10">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Locality Opportunity Map</div>
                            <h3 className="mt-2 text-xl font-semibold tracking-tight">Find pockets with high demand and low creator saturation</h3>
                            <p className="mt-3 text-slate-600">
                                Identify localities where category conversation is rising, but creator density is still low.
                                This is where hyperlocal campaigns often deliver the best cost-to-impact ratio.
                            </p>

                            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Signals</div>
                                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Local keyword growth</li>
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>UGC velocity by pocket</li>
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Creator density by tier</li>
                                    </ul>
                                </div>
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Outcome</div>
                                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Priority locality list</li>
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Budget allocation guidance</li>
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Creator sourcing plan</li>
                                    </ul>
                                </div>
                            </div>

                            <a href="#cta" className="mt-5 inline-flex items-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                                See opportunity map
                            </a>
                        </div>

                        <div>
                            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-100"></div>
                        </div>
                    </div>

                    {/* Module 2: Competitor by Pocket */}
                    <div className="grid items-center gap-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:grid-cols-2 lg:p-10">
                        <div className="order-2 lg:order-1">
                            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-100"></div>
                        </div>

                        <div className="order-1 lg:order-2">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Competitor by Pocket</div>
                            <h3 className="mt-2 text-xl font-semibold tracking-tight">See how competitors behave in each locality</h3>
                            <p className="mt-3 text-slate-600">
                                Competitors often change their message, creator tier, and content style by pocket.
                                Track where they are investing and what hooks they repeat to win local attention.
                            </p>

                            <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                <div className="text-sm font-semibold">What you can extract</div>
                                <p className="mt-2 text-sm text-slate-700">
                                    A locality-wise competitor playbook: creator types, posting cadence, local language usage, and the offers that drive reactions.
                                </p>
                            </div>

                            <a href="#cta" className="mt-5 inline-flex items-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                                Compare competitors
                            </a>
                        </div>
                    </div>

                    {/* Module 3: Local Creative Benchmarks */}
                    <div className="grid items-center gap-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:grid-cols-2 lg:p-10">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Local Creative Benchmarks</div>
                            <h3 className="mt-2 text-xl font-semibold tracking-tight">Benchmark hooks, formats, and language by geography</h3>
                            <p className="mt-3 text-slate-600">
                                A “trend” is only useful if you know where it works. Compare content patterns across city clusters and localities.
                                Translate insights into briefs for nano and micro creators.
                            </p>

                            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Benchmarks</div>
                                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Views & reach ranges (directional)</li>
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Weighted engagement (saves/shares)</li>
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Hook-to-retention patterns</li>
                                    </ul>
                                </div>
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Use for</div>
                                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Media plan KPI assumptions</li>
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Briefs & scripts by pocket</li>
                                        <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>Creator tier mix decisions</li>
                                    </ul>
                                </div>
                            </div>

                            <a href="#cta" className="mt-5 inline-flex items-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                                View benchmarks
                            </a>
                        </div>

                        <div>
                            <div className="aspect-[4/3] w-full rounded-3xl bg-slate-100"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Platform-wise Hyperlocal Lens */}
            <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:p-10">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <h2 className="text-3xl font-semibold tracking-tight">Platform-wise hyperlocal lens</h2>
                            <p className="mt-2 max-w-3xl text-slate-600">
                                Apply the same hyperlocal logic across platforms—but measure success with the right signals per platform.
                            </p>
                        </div>
                        <div className="text-xs text-slate-500">Only list platforms you genuinely support.</div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2">
                        {['instagram', 'youtube', 'facebook', 'x', 'linkedin'].map((plat) => (
                            <button
                                key={plat}
                                className={`plat-btn rounded-full border px-4 py-2 text-sm font-semibold hover:bg-slate-50 ${activePlatform === plat ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-900 border-slate-200'}`}
                                onClick={() => setActivePlatform(plat)}
                            >
                                {plat.charAt(0).toUpperCase() + plat.slice(1).replace('x', 'X (Twitter)')}
                            </button>
                        ))}
                    </div>

                    <div className="mt-6 grid gap-6 lg:grid-cols-3">
                        <div className="lg:col-span-2 rounded-3xl border border-slate-100 bg-slate-50 p-6">
                            <div className="text-lg font-semibold">{currentPlatform.title}</div>
                            <p className="mt-2 text-sm text-slate-700">
                                {currentPlatform.desc}
                            </p>

                            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                <div className="rounded-2xl border border-slate-100 bg-white p-4">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hyperlocal signals</div>
                                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                        {currentPlatform.look.map((item, i) => (
                                            <li key={i} className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>{item}</li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="rounded-2xl border border-slate-100 bg-white p-4">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Planning decisions</div>
                                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                        {currentPlatform.use.map((item, i) => (
                                            <li key={i} className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-slate-900"></span>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <a href="#cta" className="rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 text-center">
                                    Get a demo
                                </a>
                                <button className="rounded-2xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 text-center bg-transparent border-slate-200 cursor-pointer">
                                    See sample hyperlocal report
                                </button>
                            </div>
                        </div>

                        <div className="rounded-3xl border border-slate-100 bg-white p-6">
                            <div className="text-sm font-semibold">Hyperlocal presets</div>
                            <p className="mt-2 text-sm text-slate-600">Quick-start analysis views for India-first campaigns.</p>

                            <div className="mt-4 space-y-3">
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-sm font-semibold">Locality Trend Pack</div>
                                    <div className="mt-1 text-xs text-slate-600">What hooks and formats are trending in each pocket this week.</div>
                                </div>
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-sm font-semibold">Creator Density Map</div>
                                    <div className="mt-1 text-xs text-slate-600">Nano/micro availability and pricing intensity by locality.</div>
                                </div>
                                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="text-sm font-semibold">Competitor Pocket Watch</div>
                                    <div className="mt-1 text-xs text-slate-600">Where competitors are investing and what offers they push locally.</div>
                                </div>
                            </div>

                            <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Outcome</div>
                                <p className="mt-2 text-sm text-slate-600">
                                    Better briefs, better creator selection, and better locality coverage—without guessing.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <section className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 lg:px-8">
                <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:p-10">
                    <h2 className="text-2xl font-semibold tracking-tight">Questions and Answers</h2>
                    <p className="mt-2 text-slate-600">Common questions from brands and agencies planning hyperlocal campaigns.</p>

                    <div className="mt-6 divide-y divide-slate-100">
                        <details className="group py-4" open>
                            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                                What makes this different from generic “market research”?
                                <span className="text-slate-400 group-open:rotate-180 transition">▾</span>
                            </summary>
                            <p className="mt-3 text-sm text-slate-600">
                                Generic research gives broad trends. Hyperlocal analysis tells you what works in each pocket—localities, language,
                                creator density, and affordability—so your campaign plan matches ground reality.
                            </p>
                        </details>

                        <details className="group py-4">
                            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                                How does this connect to Media Plans and Outreach?
                                <span className="text-slate-400 group-open:rotate-180 transition">▾</span>
                            </summary>
                            <p className="mt-3 text-sm text-slate-600">
                                Insights inform Media Plans (creator tier mix, locality split, KPI assumptions) and Outreach (which creators to pitch first,
                                what angle to propose, and how to localize scripts).
                            </p>
                        </details>

                        <details className="group py-4">
                            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                                Can I use this for multiple cities?
                                <span class="text-slate-400 group-open:rotate-180 transition">▾</span>
                            </summary>
                            <p className="mt-3 text-sm text-slate-600">
                                Yes. The recommended setup is one dashboard per city cluster, then separate views per locality so you can execute in parallel without mixing signals.
                            </p>
                        </details>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section id="cta" className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 lg:px-8">
                <div className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-soft lg:p-10">
                        <h3 className="text-xl font-semibold tracking-tight">See hyperlocal market analysis for your category</h3>
                        <p className="mt-2 text-slate-600">
                            We will set up a sample dashboard for your category, target cities, and localities—then show how insights turn into execution-ready plans.
                        </p>
                        <button className="mt-5 inline-flex items-center rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 border-none cursor-pointer">
                            Request demo
                        </button>
                    </div>

                    <div className="rounded-3xl bg-slate-900 p-6 text-white shadow-soft lg:p-10">
                        <h3 className="text-xl font-semibold tracking-tight">Start with one pocket</h3>
                        <p className="mt-2 text-white/80">
                            Pick one city cluster and 3–5 localities for a pilot. Validate hooks, creators, and results—then scale confidently.
                        </p>
                        <button className="mt-5 inline-flex items-center rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100 border-none cursor-pointer">
                            Start pilot
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-slate-100">
                <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
                    <div className="grid gap-10 md:grid-cols-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <div className="h-9 w-9 rounded-xl bg-slate-900"></div>
                                <div className="text-base font-semibold">Oye Creators</div>
                            </div>
                            <p className="mt-3 text-sm text-slate-600">
                                Hyperlocal creator campaigns with measurable outcomes—powered by data.
                            </p>
                        </div>

                        <div>
                            <div className="text-sm font-semibold">Product</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-600">
                                <li><Link className="hover:text-slate-900" to="/product/discover-creators">Discovery</Link></li>
                                <li><Link className="hover:text-slate-900" to="/product/media-analytics">Market Analysis</Link></li>
                                <li><Link className="hover:text-slate-900" to="/product/media-plans">Media Plans</Link></li>
                                <li><Link className="hover:text-slate-900" to="/product/manage-campaigns">Outreach</Link></li>
                                <li><Link className="hover:text-slate-900" to="#">Reporting</Link></li>
                            </ul>
                        </div>

                        <div>
                            <div className="text-sm font-semibold">Company</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-600">
                                <li><Link className="hover:text-slate-900" to="#">About</Link></li>
                                <li><Link className="hover:text-slate-900" to="/contact">Contact</Link></li>
                                <li><Link className="hover:text-slate-900" to="/jobs">Careers</Link></li>
                            </ul>
                        </div>

                        <div>
                            <div className="text-sm font-semibold">Legal</div>
                            <ul className="mt-3 space-y-2 text-sm text-slate-600">
                                <li><Link className="hover:text-slate-900" to="/terms-of-service">Terms</Link></li>
                                <li><Link className="hover:text-slate-900" to="/privacy-policy">Privacy</Link></li>
                                <li><Link className="hover:text-slate-900" to="/cookies-and-ip">Cookie Policy</Link></li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-10 text-xs text-slate-500">
                        © <span id="year">{new Date().getFullYear()}</span> Oye Creators. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default MediaAnalytics;
